import { withApollo } from '@apollo/react-hoc';
import React, { Fragment, useContext, useState } from 'react';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import lightTheme from 'themes/lightTheme';
import { useRegistrationEventsCount } from 'components/Events/gql';
import { PromoterOnboardingSuccessModal } from 'components/PromoterOnboardingSuccessModal';
import PermissionModal from 'pages/track/PermissionModal/PermissionModal';
import { AppContext } from 'shared/AppContext';
import storage from 'shared/storage';
import GetStripeLogin from '../AdminLayout/gql/get-login';
import IconRail from './components/IconRail';
import SidePanel from './components/SidePanel';
import { getNavSections } from './config/getNavSections';
import useAccountSetup from './hooks/useAccountSetup';
import useActiveSection from './hooks/useActiveSection';
import {
  GlobalStyle,
  LayoutContainer,
  ContentArea,
} from './styled/NavLayout.styled';

const NavLayout = ({ children, getStripeLogin, history }) => {
  const { state, dispatch } = useContext(AppContext);

  const {
    user,
    account,
    capabilities,
    accountOptions,
    showPermissionModal,
    setShowPermissionModal,
    setSelectedAccount,
  } = useAccountSetup();

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelSection, setPanelSection] = useState(null);
  const [showStripeOnboardingModal, setShowStripeOnboardingModal] =
    useState(false);

  const role = user?.me?.role;
  const isPromoter = role === 'track' || role === 'employee';
  const { data: registrationCountData } = useRegistrationEventsCount({
    skip: !isPromoter || process.env.REACT_APP_PLATFORM === 'tickethoss',
  });
  const hasRegistrationEvents =
    (registrationCountData?.getRegistrationEventsCountByAccount ?? 0) > 0;

  const sections = getNavSections(role, capabilities, hasRegistrationEvents);
  const activeSection = useActiveSection(sections);

  // Use panelSection if user explicitly selected one, otherwise use route-derived
  const displayedSection = panelSection || activeSection;

  // A panel section with a single navigable item behaves like a direct link —
  // there's no sub-nav worth opening (e.g. TH Users with only "All Users").
  const getSinglePanelLink = (section) => {
    if (section.type !== 'section' || !Array.isArray(section.panelItems)) {
      return null;
    }
    if (section.panelItems.length !== 1) return null;
    const [item] = section.panelItems;
    const isNavigable =
      item.link &&
      item.type !== 'expandable' &&
      item.type !== 'external' &&
      item.type !== 'stripe';
    return isNavigable ? item.link : null;
  };

  const handleRailClick = (section) => {
    const singlePanelLink = getSinglePanelLink(section);

    if (section.type === 'direct' || singlePanelLink) {
      // Direct navigation — close panel and navigate
      setPanelOpen(false);
      setPanelSection(null);
      dispatch({ type: 'SET_NAV_EXPANDED', navIsExpanded: false });
      history.push(singlePanelLink || section.link);
      return;
    }

    if (section.type === 'external') {
      window.open(section.link, '_blank');
      return;
    }

    // Section with panel
    if (displayedSection === section.id && panelOpen) {
      // Same section clicked while open — collapse
      setPanelOpen(false);
      dispatch({ type: 'SET_NAV_EXPANDED', navIsExpanded: false });
    } else if (displayedSection === section.id && !panelOpen) {
      // Same section clicked while closed — expand
      setPanelOpen(true);
      dispatch({ type: 'SET_NAV_EXPANDED', navIsExpanded: true });
    } else {
      // Different section — switch and ensure open, navigate to first item
      setPanelSection(section.id);
      if (!panelOpen) {
        setPanelOpen(true);
        dispatch({ type: 'SET_NAV_EXPANDED', navIsExpanded: true });
      }
      // Navigate to the first internal panel item's link
      if (section.panelItems && section.panelItems.length > 0) {
        const firstItem = section.panelItems.find(
          (item) =>
            item.link &&
            item.type !== 'expandable' &&
            item.type !== 'external' &&
            item.type !== 'stripe'
        );
        if (firstItem) {
          history.push(firstItem.link);
        }
      }
    }
  };

  const handlePanelClose = () => {
    setPanelOpen(false);
    dispatch({ type: 'SET_NAV_EXPANDED', navIsExpanded: false });
  };

  const handlePanelOpen = () => {
    setPanelOpen(true);
    dispatch({ type: 'SET_NAV_EXPANDED', navIsExpanded: true });
  };

  const handleThemeToggle = () => {
    const newTheme = state.theme === 'light' ? 'dark' : 'light';
    dispatch({ type: 'SET_THEME', theme: newTheme });
  };

  const handleSettingsClick = () => {
    if (user?.me?.role === 'admin') {
      history.push('/admin/settings');
    } else if (user?.me?.role === 'track') {
      history.push('/admin-track/settings/0');
    } else {
      history.push('/admin-employee/settings/0');
    }
  };

  const handlePermissionDenied = () => {
    setShowPermissionModal(true);
  };

  const handleStripeClick = async () => {
    if (account?.data?.stripe_active === false) {
      setShowStripeOnboardingModal(true);
      return;
    }

    const result = await getStripeLogin();
    if (result && result.data && result.data.getStripeLogin) {
      window.location.assign(result.data.getStripeLogin);
    } else {
      history.push({
        pathname: '/stripe',
        search: '',
        state: {
          email: storage.get('user').email,
          id: storage.get('user').id,
          role: storage.get('user').role,
        },
      });
    }
  };

  const handleAccountChange = (value) => {
    setSelectedAccount(value);
    storage.set('selected_account', value);
  };

  const showAccountSelector =
    user?.me?.role !== 'admin' && user?.me?.accounts?.length > 1;

  if (!user) return null;

  const navTheme = state.theme === 'light' ? lightTheme : darkTheme;

  return (
    <Fragment>
      <LayoutContainer>
        <IconRail
          sections={sections}
          activeSection={displayedSection}
          panelOpen={panelOpen}
          onRailClick={handleRailClick}
          onThemeToggle={handleThemeToggle}
          onSettingsClick={handleSettingsClick}
          onPermissionDenied={handlePermissionDenied}
          user={user?.me}
        />

        <ThemeProvider theme={navTheme}>
          <SidePanel
            isOpen={panelOpen}
            activeSection={displayedSection}
            sections={sections}
            onClose={handlePanelClose}
            onOpen={handlePanelOpen}
            showAccountSelector={showAccountSelector}
            account={account}
            accountOptions={accountOptions}
            onAccountChange={handleAccountChange}
            onStripeClick={handleStripeClick}
            onPermissionDenied={handlePermissionDenied}
          />
        </ThemeProvider>

        <ContentArea panelOpen={panelOpen}>
          {children}
          <PermissionModal
            isVisible={showPermissionModal}
            hideModal={() => setShowPermissionModal(false)}
            link={
              user?.me?.role === 'track'
                ? '/admin-track/settings/1'
                : '/admin-employee/settings/1'
            }
            history={history}
          />
          <PromoterOnboardingSuccessModal
            isVisible={showStripeOnboardingModal}
            hideModal={() => setShowStripeOnboardingModal(false)}
            businessName={account?.label}
            accountId={account?.value}
          />
        </ContentArea>
      </LayoutContainer>
      <GlobalStyle />
    </Fragment>
  );
};

export default compose(withRouter, withApollo, GetStripeLogin)(NavLayout);
